import axios from "axios";
import { store, clearPersistedData } from "../../store/configureStore";
import { updateToken, deAuthenticate } from "../../store/reducers/authSlice";
import { PURGE_Brand } from "../../store/reducers/brandSlice";
import { setUser } from "../../store/reducers/userSlice";
import { refreshTokenAPI } from "./auth";

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

export const setupAxiosInterceptors = () => {
  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // Handle 401 or 403 errors
      if (
        error.response &&
        (error.response.status === 401 || error.response.status === 403)
      ) {
        const errorDetail = error?.response?.data?.detail;

        // A temporary password is still in use: not an auth failure, so don't log out.
        // Flag the user so the route guard shows the "set a new password" screen.
        if (error.response.status === 403 && errorDetail === "password-change-required") {
          store.dispatch(setUser({ must_change_password: true }));
          return Promise.reject(error);
        }

        // If it's a 401 status, the refresh token route itself returns an error, or explicitly Not Allowed, logout securely.
        if (
          error.response.status === 401 ||
          originalRequest.url.includes("auth/refresh/") ||
          errorDetail === "Not Allowed"
        ) {
          store.dispatch(deAuthenticate());
          store.dispatch(PURGE_Brand());
          clearPersistedData();
          window.location.reload();
          return Promise.reject(error);
        }

        // Proceed if the request has not been retried yet
        if (!originalRequest._retry) {
          originalRequest._retry = true;

          const state = store.getState();
          const refreshToken = state?.auth?.refreshToken;
          const rememberMe = state?.auth?.rememberMe;

          // If we don't have a refresh token (or user didn't check remember me), logout
          if (!refreshToken || !rememberMe) {
            store.dispatch(deAuthenticate());
            store.dispatch(PURGE_Brand());
            clearPersistedData();
            window.location.reload();
            return Promise.reject(error);
          }

          // If already refreshing, queue the request until refresh finishes
          if (isRefreshing) {
            return new Promise(function (resolve, reject) {
              failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                originalRequest.headers["Authorization"] = "Bearer " + token;
                return axios(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          // Lock the refresh loop
          isRefreshing = true;

          try {
            const response = await refreshTokenAPI(refreshToken);
            
            if (response.status === 200 || response.status === 201) {
              const newToken = response.data.idToken;
              const newRefreshToken = response.data.refreshToken;
              
              store.dispatch(
                updateToken({
                  idToken: newToken,
                  refreshToken: newRefreshToken,
                })
              );

              // Update global axios default header for future non-config requests
              axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;

              // Retry the failed request immediately
              originalRequest.headers["Authorization"] = "Bearer " + newToken;
              processQueue(null, newToken);
              
              isRefreshing = false;
              
              // Proceed with the original request using the new token
              return axios(originalRequest);
            }
          } catch (refreshError) {
            processQueue(refreshError, null);
            isRefreshing = false;

            // Refresh failed (e.g. refresh token expired), log out
            store.dispatch(deAuthenticate());
            store.dispatch(PURGE_Brand());
            clearPersistedData();
            window.location.reload();

            return Promise.reject(refreshError);
          }
        }
      }

      // Return any other errors to the calling function
      return Promise.reject(error);
    }
  );
};
